import { useMemoizedFn } from 'ahooks';
import { AutoComplete, Form, Input } from 'antd';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { UserContactAddress } from 'domains/orgDomain';
import { PlatformRoleEnum, SiteRoleEnum } from 'domains/roleDomain';
import { CSSProperties, memo, useEffect, useRef, useState } from 'react';
import accountService from 'services/accountService';
import states from 'states-us';
import { INPUT_NORMAL_MAX_LENGTH } from 'utils/constants';
import { isUnitedStates } from 'utils/address';
import { RevxErrorMessage } from 'utils/errorCodes';
import { RULE_TEXT_LEN_255 } from 'utils/validators';
import './InviteUserForm.less';

type Props = {
  formStyle?: CSSProperties;
  supplierOrgId: number;
  supplierCompany: string;
  onUserInvited?: (user: UserContactAddress, orgId: number) => void;
  onClose?: () => void;
};

export interface AddrFormValues {
  company: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  country: string;
  firstName: string;
  lastName: string;
  postalCode: string;
  state: string;
  phoneNumber: string;
  email: string;
}
export const InviteUserForm: React.FC<Props> = memo(
  ({
    formStyle = { top: 130 },
    supplierOrgId,
    supplierCompany,
    onUserInvited,
    onClose,
  }) => {
    const [form] = Form.useForm();
    const [error, setError] = useState('');
    const [adding, setAdding] = useState(false);
    const [formError, setFormError] = useState<string>('');
    const [isSubmit, setIsSubmit] = useState(false);
    const [stateOptions, setStateOptions] = useState<{ value: string }[]>(
      states.map((item) => {
        return { value: item.abbreviation };
      })
    );

    const mapRef = useRef<HTMLInputElement>(null);

    const checkFormError = useMemoizedFn(async (isSubmit: Boolean) => {
      try {
        if (isSubmit) {
          await form.validateFields();
        }
        setFormError('');
      } catch (e: any) {
        if (e?.errorFields?.length) {
          setFormError(
            `Don’t forget to enter this information: ${e?.errorFields
              .map((item: { errors: string[] }) =>
                item.errors[0].replace('Please enter ', '')
              )
              .join(', ')}`
          );
        } else {
          setFormError('');
        }
      }
    });

    useEffect(() => {
      if (
        mapRef.current &&
        (window as any).google &&
        (window as any).google.maps
      ) {
        const autocomplete = new (
          window as any
        ).google.maps.places.Autocomplete(mapRef.current, {
          componentRestrictions: { country: ['us', 'pr'] },
          fields: ['address_components'],
        });
        // When the user selects an address from the drop-down, populate the
        // address fields in the form.
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          const data = form.getFieldsValue();
          const autoCompleteData: { [x: string]: string | undefined } = {
            addressLine1: '',
            // country: '',
            // postalCode: '',
            // state: '',
            // city: '',
          };
          for (const component of place.address_components) {
            const componentType = component.types[0];
            switch (componentType) {
              case 'street_number': {
                autoCompleteData.addressLine1 = `${component.long_name} ${autoCompleteData.addressLine1}`;
                break;
              }
              case 'route': {
                autoCompleteData.addressLine1 += component.long_name;
                break;
              }
              case 'country': {
                autoCompleteData.country = component.short_name;
                break;
              }
              case 'postal_code': {
                autoCompleteData.postalCode = component.long_name;
                break;
              }
              case 'administrative_area_level_1': {
                autoCompleteData.state = component.short_name;
                break;
              }
              case 'locality': {
                autoCompleteData.city = component.long_name;
                break;
              }
            }
          }
          form.setFieldsValue({ ...data, ...autoCompleteData });
          checkFormError(isSubmit);
        });
      }
    }, [mapRef, form, checkFormError, isSubmit]);

    return (
      <div>
        <Form
          form={form}
          style={formStyle}
          requiredMark={false}
          layout="vertical"
          className="job-config-color-form invite-user-form job-config-dropdown"
          initialValues={{
            company: supplierCompany,
          }}
          autoComplete="off"
          onFinish={async (values: AddrFormValues) => {
            setAdding(true);
            try {
              // create contact with address
              let invitedUser = await accountService.inviteUser({
                orgId: supplierOrgId,
                siteRole: SiteRoleEnum.Supplier,
                platformRole: PlatformRoleEnum.NoAdminAccess,
                firstName: values.firstName,
                lastName: values.lastName,
                company: values.company,
                phoneNumber: values.phoneNumber,
                email: values.email,
                addressLine1: values.addressLine1,
                addressLine2: values.addressLine2,
                city: values.city,
                state: values.state,
                country: values.country,
                postalCode: values.postalCode,
              });

              if (onUserInvited) {
                await onUserInvited(invitedUser, supplierOrgId);
              }
            } catch (e) {
              setError((e as RevxErrorMessage).message);
            }
            setAdding(false);
            if (onClose) {
              onClose();
            }
          }}
          onFinishFailed={() => {
            setIsSubmit(true);
            checkFormError(true);
          }}
          onValuesChange={() => checkFormError(isSubmit)}
        >
          {Boolean(formError) && (
            <div
              className="ant-form-item-explain-error"
              style={{ marginBottom: 10 }}
            >
              {formError}
            </div>
          )}
          <RevxFlexLayout align="top" style={{ columnGap: 0 }}>
            <RevxFlexLayout align="top" direction="column">
              <RevxFlexLayout align="top">
                <Form.Item
                  label="First name"
                  help={false}
                  name="firstName"
                  rules={[RULE_TEXT_LEN_255, { required: true }]}
                >
                  <Input
                    // placeholder="First Name"
                    style={{ width: 181 }}
                    maxLength={INPUT_NORMAL_MAX_LENGTH}
                  />
                </Form.Item>
                <Form.Item
                  label="Last name"
                  help={false}
                  name="lastName"
                  rules={[RULE_TEXT_LEN_255, { required: true }]}
                >
                  <Input
                    style={{ width: 181 }}
                    // placeholder="Last Name"
                    maxLength={INPUT_NORMAL_MAX_LENGTH}
                  />
                </Form.Item>
              </RevxFlexLayout>
              <RevxFlexLayout align="top">
                <Form.Item
                  label="Email address"
                  help={false}
                  name="email"
                  rules={[{ required: true }]}
                >
                  <Input
                    style={{ width: 181 }}
                    maxLength={INPUT_NORMAL_MAX_LENGTH}
                  />
                </Form.Item>
                <Form.Item
                  label="Phone number"
                  help={false}
                  rules={[{ required: true }]}
                  name="phoneNumber"
                >
                  <Input
                    style={{ width: 181 }}
                    maxLength={INPUT_NORMAL_MAX_LENGTH}
                  />
                </Form.Item>
              </RevxFlexLayout>
            </RevxFlexLayout>
            <RevxFlexLayout
              align="top"
              direction="column"
              className="job-config-new-contact"
            >
              <RevxFlexLayout align="top">
                <Form.Item
                  label="Company name"
                  name="company"
                  help={false}
                  rules={[RULE_TEXT_LEN_255, { required: true }]}
                >
                  <Input
                    style={{ width: 401 }}
                    maxLength={INPUT_NORMAL_MAX_LENGTH}
                  />
                </Form.Item>
              </RevxFlexLayout>
              <RevxFlexLayout align="top">
                <Form.Item
                  label="Company Shipping Address"
                  help={false}
                  rules={[{ required: true }]}
                  name="addressLine1"
                >
                  {/* <Input style={{ width: 401 }} ref={mapRef} /> */}
                  <input
                    className="ant-input pac-target-input"
                    maxLength={INPUT_NORMAL_MAX_LENGTH}
                    style={{ width: 401 }}
                    ref={mapRef}
                    placeholder=""
                  />
                </Form.Item>
              </RevxFlexLayout>
              <RevxFlexLayout align="top">
                <Form.Item
                  label="Company Shipping Address 2(Suite, Room, Floor, etc.)(optional)"
                  help={false}
                  name="addressLine2"
                  rules={[{ required: false }]}
                >
                  <Input
                    style={{ width: 401 }}
                    maxLength={INPUT_NORMAL_MAX_LENGTH}
                  />
                </Form.Item>
              </RevxFlexLayout>

              <RevxFlexLayout>
                <Form.Item
                  label="City"
                  name="city"
                  rules={[{ required: true }]}
                  help={false}
                >
                  <Input
                    style={{ width: 190 }}
                    maxLength={INPUT_NORMAL_MAX_LENGTH}
                  />
                </Form.Item>
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, cur) => prev.country !== cur.country}
                >
                  {({ getFieldValue }) => (
                <Form.Item
                  label="State/Province"
                  help={false}
                  name="state"
                  // only US addresses need a state; most countries have none (PRD-746).
                  // `dependencies` re-validates when country changes, and the rule reads the
                  // country as it validates, so switching to a country without states clears
                  // the error instead of leaving it stuck.
                  dependencies={['country']}
                  required={isUnitedStates(getFieldValue('country'))}
                  rules={[
                    ({ getFieldValue: getCurrent }) => ({
                      required: isUnitedStates(getCurrent('country')),
                    }),
                  ]}
                >
                  {/* the suggestions are US state codes, so offer them only for US
                      addresses; elsewhere a province is free text (PRD-746) */}
                  {isUnitedStates(getFieldValue('country')) ? (
                    <AutoComplete
                      style={{ width: 190 }}
                      options={stateOptions}
                      onSearch={(searchText: string) => {
                        const allStates = states.map((item) => {
                          return { value: item.abbreviation };
                        });
                        setStateOptions(
                          !searchText
                            ? []
                            : allStates.filter((s) =>
                                s.value.includes(searchText.toUpperCase())
                              )
                        );
                      }}
                      maxLength={INPUT_NORMAL_MAX_LENGTH}
                    />
                  ) : (
                    <Input
                      style={{ width: 190 }}
                      maxLength={INPUT_NORMAL_MAX_LENGTH}
                    />
                  )}
                </Form.Item>
                  )}
                </Form.Item>
              </RevxFlexLayout>
              <RevxFlexLayout align="top">
                <Form.Item
                  label="Zip Code"
                  help={false}
                  rules={[{ required: true }]}
                  name="postalCode"
                >
                  <Input
                    style={{ width: 190 }}
                    maxLength={INPUT_NORMAL_MAX_LENGTH}
                  />
                </Form.Item>
              </RevxFlexLayout>
              <RevxFlexLayout align="top">
                <Form.Item
                  label="Country"
                  name="country"
                  help={false}
                  rules={[{ required: true }]}
                >
                  <Input
                    style={{ width: 401 }}
                    maxLength={INPUT_NORMAL_MAX_LENGTH}
                  />
                </Form.Item>
              </RevxFlexLayout>
            </RevxFlexLayout>
          </RevxFlexLayout>

          <RevxFlexLayout>
            <RevxButton
              type="primary"
              loading={adding}
              brandOrange
              htmlType="submit"
              className="invite-user-form-btn"
            >
              Invite
            </RevxButton>
            <RevxButton
              disabled={adding}
              onClick={(event) => {
                event.stopPropagation();
                if (onClose) {
                  onClose();
                }
              }}
              brandOrange
              className="invite-user-form-btn invite-user-form-cancel-btn"
            >
              Cancel
            </RevxButton>
          </RevxFlexLayout>
          {error && (
            <div
              className="ant-form-item-explain-error"
              style={{ marginTop: 10 }}
            >
              {error}
            </div>
          )}
        </Form>
      </div>
    );
  }
);
